import { useCallback, useEffect, useMemo, useState } from "react";
import {
  bomSelectedCategoryNameState,
  forgeCategories,
  forgePropertyDataState,
  forgeRowDataState,
  forgeToolbarCreatedState,
  forgeViewerState,
  isForgeViewLoadingState,
} from "../atoms";
import { useAtom } from "jotai";
import { useRecoilState, useRecoilValue } from "recoil";
import { COLOR } from "../constants/color";

const customPropertyPanelCompoundLayerExtension = "CustomPropertyPanelCompoundLayerExtension";

function useForgeView() {
  const [forgeViewer, setForgeViewer] = useAtom(forgeViewerState);
  const [modelDataLoading, setModelDataLoading] = useState(false);

  const [isForgeViewLoading, setIsForgeViewLoading] = useAtom(isForgeViewLoadingState);
  const [forgeToolbarCreated, setForgeToolbarCreated] = useAtom(forgeToolbarCreatedState);
  const [modelPropertyData, setModelPropertyData] = useAtom(forgePropertyDataState);
  const [rowModalData, setRowModalData] = useAtom(forgeRowDataState);
  const selectedCategoryName = useRecoilValue(bomSelectedCategoryNameState);
  const [forgeCategoriesState, setForgeCategories] = useRecoilState(forgeCategories);
  const [instanceTree, setInstanceTree] = useState()

  const updateInstanceTree = useCallback((model = forgeViewer?.model) => {
    setInstanceTree(model?.getData()?.instanceTree)
  }, [forgeViewer])

  //handle change in viewer
  const handleChangeViewer = useCallback((viewer) => {
    setForgeViewer(viewer);
  }, [setForgeViewer]);

  // handle change loading of 3D view
  const changeForgeViewerLoading = useCallback((val) => {
    setIsForgeViewLoading(val);
  }, [setIsForgeViewLoading]);

  const initForgeViewer = useCallback(() => {
    if (window?.Autodesk?.Viewing) {
      const Autodesk = window.Autodesk;
      class CustomPropertyPanel extends Autodesk.Viewing.Extensions
        .ViewerPropertyPanel {
        constructor(viewer, options) {
          super(viewer, options);
          this._data = null;
        }
        setAggregatedProperties(propertySet) {
          console.log(
            "🚀 ~ setAggregatedProperties ~ setAggregatedProperties:"
          );
          Autodesk.Viewing.Extensions.ViewerPropertyPanel.prototype.setAggregatedProperties.call(
            this,
            propertySet
          );

          if (this._data !== null) {
            this.viewer.getProperties(this.propertyNodeId, (props) => {
              const externalId = props.externalId;

              this._data.forEach((d) => {
                if (d.uniqueId !== externalId) return;

                d.layers.forEach((s) => {
                  this.addProperty(s.material, s.thickness, "Compound Layer");
                });
              });
            });
          }
        }

        setNodeProperties(nodeId) {
          Autodesk.Viewing.Extensions.ViewerPropertyPanel.prototype.setNodeProperties.call(
            this,
            nodeId
          );
          this.nodeId = nodeId; // store the dbId for later use
        }
      }

      class CustomPropertyPanelExtension extends Autodesk.Viewing.Extension {
        constructor(viewer, options) {
          super(viewer, options);
          this._connection = null;
          this._connectionId = null;
          this._panel = null;
          this._data = null;
        }

        load() {
          console.log("🚀 ~ CustomPropertyPanelExtension ~ load ~ load:");
          if (this.options === null) return; // not a supported format
          // var params = this.options.itemId.split("/");
          // this.options.projectId = params[params.length - 3];

          // this._panel._data = JSON.parse(res);

          return true;
        }

        unload() {
          console.log("🚀 ~ CustomPropertyPanelExtension ~ unload ~ unload:");
          if (this._panel === null) return;
          var ext = this.viewer.getExtension("Autodesk.PropertiesManager");
          this._panel = null;
          ext.setDefaultPanel();
          return true;
        }

        onToolbarCreated() {
          console.log(
            "🚀 ~ CustomPropertyPanelExtension ~ onToolbarCreated ~ onToolbarCreated:"
          );
          this._panel = new CustomPropertyPanel(this.viewer, this.options);
          this.viewer.addEventListener(
            Autodesk.Viewing.EXTENSION_LOADED_EVENT,
            (e) => {
              if (e.extensionId !== "Autodesk.PropertiesManager") return;
              var ext = this.viewer.getExtension("Autodesk.PropertiesManager");
              ext.setPanel(this._panel);
              setForgeToolbarCreated(true);
            }
          );
        }

        changeCompoundLayerData(data) {
          if (this._panel) {
            this._panel._data = data;
          }
        }
      }

      window.Autodesk.Viewing.theExtensionManager.registerExtension(customPropertyPanelCompoundLayerExtension, CustomPropertyPanelExtension);

      let viewer = new window.Autodesk.Viewing.GuiViewer3D(
        document.getElementById("forgeViewer"),
        {
          extensions: ["Autodesk.VisualClusters", customPropertyPanelCompoundLayerExtension],
          selectionType: window.Autodesk.Viewing.SelectionType.OVERLAYED,
        }
      );
      const startedCode = viewer.start();
      if (startedCode > 0) {
        handleChangeViewer(undefined);
        console.error("Failed to create a Viewer: WebGL not supported.");
        return;
      }

      window.viewer = viewer; //For debugging
      return viewer;
    }
  }, [handleChangeViewer, setForgeToolbarCreated]);

  const loadUrn = useCallback((urn, viewId) => {
    if (window?.Autodesk?.Viewing) {
      window.Autodesk.Viewing.Document.load(
        `urn:${urn}`,
        (viewerDocument) => {
          changeForgeViewerLoading(true);
          const viewerapp = viewerDocument.getRoot();

          var defaultModel = viewerapp.getDefaultGeometry();
          if (forgeViewer && viewerapp && defaultModel) {
            const viewables = viewerapp.search({ type: "geometry" })

            const viewable = viewables.find((viewable) => {
              const childs = Array.isArray(viewable?.data?.children) ? viewable.data.children : [];
              return !!childs.find((child) => child.guid === viewId);
            });

            if (viewId && viewable) {
              forgeViewer.loadDocumentNode(viewerDocument, viewable);
            } else {
              forgeViewer.loadDocumentNode(viewerDocument, defaultModel);
            }
            forgeViewer.setSelectionColor(new window.THREE.Color(COLOR.BLUE_HIGHLIGHT));
            forgeViewer.loadExtension(customPropertyPanelCompoundLayerExtension, { versionId: atob(urn.replace("_", "/")) });
          }
        }
      );
    }
  }, [forgeViewer, changeForgeViewerLoading]);

  const getTreeComponents = useCallback(() => {
    // from https://learnforge.autodesk.io/#/viewer/extensions/panel?id=enumerate-leaf-nodes
    return new Promise((resolve, reject) => {
      if (!forgeViewer) {
        reject("Viewer not defined");
      }
      forgeViewer.getObjectTree(function (tree) {
        const leaves = [];
        const forgeCategoriesData = {};
        const rootId = tree.getRootId();
        tree.enumNodeChildren(
          rootId,
          function (dbId) {
            const categoryId = tree.getNodeParentId(dbId);
            if (categoryId === rootId && !forgeCategoriesData[dbId]) {
              forgeCategoriesData[dbId] = tree.getNodeName(dbId);
            }
            if (tree.getChildCount(dbId) === 0) {
              leaves.push(dbId);
            }
          },
          true
        );
        resolve({ leaves, forgeCategoriesData });
      });
    });
  }, [forgeViewer]);

  const refreshModelData = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      try {
        setModelDataLoading(true);
        window.modelPropertyData = {};
        window.rowModalData = {};
        const { leaves: dbIds, forgeCategoriesData } =
          await getTreeComponents();
        let count = dbIds.length;

        //update category state start

        setForgeCategories(forgeCategoriesData);

        //update category state end

        dbIds.forEach(function (dbId) {
          forgeViewer.getProperties(dbId, function (props) {
            window.rowModalData[dbId] = props;
            props.properties.forEach(function (prop) {
              if (!isNaN(prop.displayValue)) return; // Not categorize properties that store numbers

              // some adjustments for revit:
              prop.displayValue = prop.displayValue.replace("Revit ", ""); // remove this Revit prefix
              if (prop.displayValue.indexOf("<") === 0) return; // skip categories that start with <
              if (prop.displayName === "viewable_in") return;

              // map the data in one variable

              if (window.modelPropertyData[prop.displayName] == null)
                window.modelPropertyData[prop.displayName] = {};
              if (
                window.modelPropertyData[prop.displayName][prop.displayValue] ==
                null
              )
                window.modelPropertyData[prop.displayName][prop.displayValue] =
                  [];
              window.modelPropertyData[prop.displayName][
                prop.displayValue
              ].push(dbId);
            });

            forgeViewer.getObjectTree(function (tree) {
              // for revit models
              const typeId = tree.getNodeParentId(props.dbId);
              const familyId = tree.getNodeParentId(typeId);

              // const typeName = tree.getNodeName(typeId);
              const familyName = tree.getNodeName(familyId);

              if (window.modelPropertyData["Family Name"] == null)
                window.modelPropertyData["Family Name"] = {};
              if (window.modelPropertyData["Family Name"][familyName] == null)
                window.modelPropertyData["Family Name"][familyName] = [];
              window.modelPropertyData["Family Name"][familyName].push(
                props.dbId
              );
            });

            if (--count === 0) {
              setRowModalData(window.rowModalData);
              setModelPropertyData(window.modelPropertyData);
              setModelDataLoading(false);
              return resolve();
            }
          });
        });
      } catch (error) {
        console.log("Error in generating propertiesData", error);
        reject(error);
      }
    });
  }, [
    forgeViewer,
    setModelPropertyData,
    getTreeComponents,
    setRowModalData,
    setForgeCategories,
  ]);

  const hasProperty = useCallback((propertyName) => {
    return modelPropertyData[propertyName] !== undefined;
  }, [modelPropertyData]);

  const getLabels = useCallback((propertyName) => {
    return Object.keys(modelPropertyData[propertyName]);
  }, [modelPropertyData]);

  const getInstances = useCallback((propertyName, propertyValue) => {
    if (propertyValue) {
      return modelPropertyData[propertyName][propertyValue];
    }
    return Object.values(modelPropertyData[propertyName]).flat();
  }, [modelPropertyData]);

  const getIds = useCallback((propertyName, propertyValue) => {
    return modelPropertyData[propertyName][propertyValue];
  }, [modelPropertyData]);

  const getPropertiesNames = useCallback(() => {
    return Object.keys(modelPropertyData);
  }, [modelPropertyData]);

  const getObjectDetails = useCallback((id) => {
    return rowModalData[id];
  }, [rowModalData]);

  const getForgeDisplayUnit = useCallback(
    (autodeskUnit) => {
      if (forgeViewer?.model) {
        return forgeViewer.model.getDisplayUnit(autodeskUnit);
      }
    },
    [forgeViewer]
  );



  const getForgeParentCategoryId = useCallback(
    (id) => {
      if (forgeViewer?.model && forgeViewer.model.isNodeExists(id)) {
        while (id !== 0) {
          if (forgeCategoriesState[id]) {
            return id;
          }
          id = forgeViewer.model.getData().instanceTree.getNodeParentId(id);
        }
      }
      return undefined;
    },
    [forgeViewer, forgeCategoriesState]
  );

  //This will fetch array of ids from forge to react to that object from top to bottom
  const getForgeParentIds = useCallback(
    (id) => {
      const parentIds = [];
      let idTofetch = id;

      if (!isForgeViewLoading && forgeViewer?.model && forgeViewer.model.isNodeExists(id)) {
        const rootId = forgeViewer.model.getData().instanceTree.getRootId();

        do {
          idTofetch = forgeViewer.model.getData().instanceTree.getNodeParentId(idTofetch);
          if (idTofetch === rootId) {
            break;
          } else {
            parentIds.push(idTofetch);
          }
        } while (forgeViewer.model.isNodeExists(idTofetch));
      }
      parentIds.reverse();
      return parentIds;
    },
    [forgeViewer, isForgeViewLoading]
  );

  const filterIdsByContext = useCallback(
    (ids, contextId) => {
      if (!contextId || !forgeViewer?.model) {
        return ids;
      }
      const rootId = forgeViewer.model.getRootId();

      return ids.filter(
        (id) => {
          if (forgeViewer.model.isNodeExists(id)) {
            while (id !== rootId) {
              if (id === contextId) {
                return true
              }
              id = forgeViewer.model.getData().instanceTree.getNodeParentId(id);
            }
          }
          return false
        }
      );
    },
    [forgeViewer]
  );

  const filterIdsByCategory = useCallback((ids) => {
    if (!selectedCategoryName) {
      return ids;
    }
    return ids.filter((id) => (forgeCategoriesState[getForgeParentCategoryId(id)] === selectedCategoryName));
  }, [selectedCategoryName, forgeCategoriesState, getForgeParentCategoryId]);

  const onGeometryLoadedCallback = useCallback(() => {
    changeForgeViewerLoading(false);
  }, [changeForgeViewerLoading]);

  const onObjectTreeCreatedCallback = useCallback(() => {
    refreshModelData();
    updateInstanceTree();
  }, [refreshModelData, updateInstanceTree]);

  const onSelectionChangeCallback = useCallback((event) => (new Promise((resolve) => {
    if (event.dbIdArray.length === 1) {
      const dbId = event.dbIdArray[0];
      const data = getObjectDetails(dbId);
      if (data) {
        resolve(data);
        return;
      }
      if (forgeViewer) {
        forgeViewer.getProperties(dbId, (properties) => {
          resolve(properties);
          return;
        });
      }
    } else {
      resolve(null);
      return;
    }
  })), [forgeViewer, getObjectDetails]);

  const loadCompoundLayerData = useCallback((data) => {
    if (forgeToolbarCreated && forgeViewer) {
      const customExtension = forgeViewer.getExtension(customPropertyPanelCompoundLayerExtension);
      customExtension && customExtension.changeCompoundLayerData(data);
    }
  }, [forgeToolbarCreated, forgeViewer]);

  const getExternalIdMapper = useCallback(() => (new Promise((resolve) => {
    if (forgeViewer?.model?.getExternalIdMapping && !isForgeViewLoading) {
      forgeViewer.model.getExternalIdMapping(resolve, (error) => {
        console.log("getExternalIdMapper Error", error);
        resolve(null);
      });
    } else {
      resolve(null);
    }
  })), [forgeViewer, isForgeViewLoading]);

  const selectedCategoryId = useMemo(() => {
    const categoryId = Object.keys(forgeCategoriesState).find((id) => selectedCategoryName === forgeCategoriesState[id]);
    if (categoryId && !isNaN(categoryId)) {
      return +categoryId;
    }
    return null;
  }, [forgeCategoriesState, selectedCategoryName])

  useEffect(() => {
    updateInstanceTree();
  }, [updateInstanceTree])


  return {
    forgeViewer,
    modelDataLoading,
    modelPropertyData,
    selectedCategoryName,
    isForgeViewLoading,
    instanceTree,
    selectedCategoryId,
    handleChangeViewer,
    getInstances,
    getLabels,
    getIds,
    hasProperty,
    getPropertiesNames,
    getObjectDetails,
    getForgeDisplayUnit,
    filterIdsByCategory,
    changeForgeViewerLoading,
    loadUrn,
    initForgeViewer,
    getForgeParentCategoryId,
    onGeometryLoadedCallback,
    onObjectTreeCreatedCallback,
    onSelectionChangeCallback,
    loadCompoundLayerData,
    getExternalIdMapper,
    getForgeParentIds,
    filterIdsByContext,
  };
}

export default useForgeView;
